/**
 * Market selector (Capital theme adaptation).
 * - Opens a self-contained overlay popup listing configured markets.
 * - Redirects to the chosen market's URL.
 * - Optionally auto-opens once per visitor on first visit (cookie-gated).
 * - Clones the header trigger into the mobile navigation drawer footer.
 */
(function () {
  'use strict';

  var MODAL_ID = 'header-market-popup';
  var DISMISS_COOKIE = 'jeberg_market_dismissed';
  var COOKIE_DAYS = 365;

  /**
   * @param {string} name
   * @returns {?string}
   */
  function getCookie(name) {
    var match = ('; ' + document.cookie).split('; ' + name + '=');
    if (match.length === 2) return match.pop().split(';').shift();
    return null;
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {number} days
   */
  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 86400000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + value + expires + '; path=/; SameSite=Lax';
  }

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  /**
   * Moves the popup to <body> so it escapes the header's stacking context,
   * otherwise the sticky header / cart drawer can paint over it.
   * Also removes any stale copy left behind by a theme-editor section reload.
   */
  function portalModal() {
    // Remove any previously portaled copy first so a theme-editor reload (which
    // re-renders a fresh modal inside the header) never leaves duplicate ids.
    document.querySelectorAll('body > [data-market-portaled]').forEach(function (stale) {
      stale.remove();
    });
    var modal = getModal();
    if (modal && modal.parentNode !== document.body) {
      modal.setAttribute('data-market-portaled', '');
      document.body.appendChild(modal);
    }
  }

  function openPopup() {
    var modal = getModal();
    if (!modal) return;
    modal.hidden = false;
    // Force reflow so the transition runs, then flip the open state.
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.body.classList.add('market-modal-open');
    document.addEventListener('keydown', onKeydown);
    var win = modal.querySelector('.market-modal__window');
    if (win) win.focus();
  }

  function closePopup() {
    var modal = getModal();
    if (!modal) return;
    modal.classList.remove('is-open');
    document.body.classList.remove('market-modal-open');
    document.removeEventListener('keydown', onKeydown);
    var hide = function () {
      modal.hidden = true;
      modal.removeEventListener('transitionend', hide);
    };
    modal.addEventListener('transitionend', hide);
    // Fallback in case transitionend doesn't fire.
    setTimeout(function () {
      if (!modal.classList.contains('is-open')) modal.hidden = true;
    }, 350);
  }

  function onKeydown(evt) {
    if (evt.key === 'Escape' || evt.key === 'Esc') {
      dismiss();
      closePopup();
    }
  }

  function dismiss() {
    setCookie(DISMISS_COOKIE, '1', COOKIE_DAYS);
  }

  /**
   * @param {HTMLElement} btn
   */
  function redirectFromButton(btn) {
    var url = btn.getAttribute('data-market-url');
    if (!url) return;
    dismiss();
    window.location.href = url;
  }

  /**
   * Wires open handlers. Safe to call more than once for cloned nodes.
   * @param {ParentNode} root
   */
  function bindTriggers(root) {
    root.querySelectorAll('[data-market-open]').forEach(function (btn) {
      if (btn.dataset.marketBound) return;
      btn.dataset.marketBound = '1';
      btn.addEventListener('click', function (evt) {
        evt.preventDefault();
        openPopup();
      });
    });
  }

  function bindPopup() {
    var modal = getModal();
    if (!modal || modal.dataset.marketBound) return;
    modal.dataset.marketBound = '1';

    modal.querySelectorAll('[data-market-url]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        redirectFromButton(btn);
      });
    });

    modal.querySelectorAll('[data-market-stay]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        dismiss();
        closePopup();
      });
    });
  }

  function maybeAutoOpen() {
    if (window.Shopify && window.Shopify.designMode) return;
    var modal = getModal();
    if (!modal || !modal.hasAttribute('data-market-auto-open')) return;
    if (getCookie(DISMISS_COOKIE)) return;
    openPopup();
  }

  /**
   * Clones the header trigger into the mobile navigation drawer footer so the
   * market selector is reachable from the mobile menu.
   */
  function cloneToMobile() {
    var headerWrapper = document.querySelector('.site-header [data-market-wrapper]');
    var mobileFooter = document.querySelector('.mobile-nav--footer');
    if (!headerWrapper || !mobileFooter) return;
    if (mobileFooter.querySelector('[data-market-wrapper]')) return;

    var clone = headerWrapper.cloneNode(true);
    delete clone.dataset.marketBound;
    clone.querySelectorAll('[data-market-open]').forEach(function (btn) {
      delete btn.dataset.marketBound;
    });

    mobileFooter.insertBefore(clone, mobileFooter.firstChild);
    bindTriggers(clone);
  }

  function init() {
    if (!getModal()) return;
    portalModal();
    bindTriggers(document);
    bindPopup();
    cloneToMobile();
    maybeAutoOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after a header re-render in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();